import React, { useCallback, useEffect, useRef, useState } from 'react';
import { User, Image, XCircle } from 'lucide-react';
import { Button } from '../ui/button';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { adminGet, adminDelete, adminUpload, reportError, AGENCY_ID } from './adminApi';

export function AdminTeamTab({ adminKey, isSuperAdmin }) {
  const [members, setMembers] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ agency: AGENCY_ID, prenom: '', fonction: '', order: 0 });
  const [photo, setPhoto] = useState(null);
  const [logoSent, setLogoSent] = useState(false);
  const photoRef = useRef(null);
  const logoRef = useRef(null);

  const fetchTeam = useCallback(async () => {
    setError('');
    try {
      const res = await adminGet('/api/admin/team', adminKey);
      setMembers(res.data.members || []);
    } catch (err) {
      setError(reportError("chargement de l'équipe", err) || 'Erreur de chargement');
    }
  }, [adminKey]);

  useEffect(() => { fetchTeam(); }, [fetchTeam]);

  const addMember = async () => {
    if (!form.prenom.trim() || !form.fonction.trim()) { toast.error('Prénom et fonction obligatoires'); return; }
    try {
      const fd = new FormData();
      fd.append('agency', form.agency);
      fd.append('prenom', form.prenom);
      fd.append('fonction', form.fonction);
      fd.append('order', form.order);
      if (photo) fd.append('photo', photo);
      await adminUpload('post', '/api/admin/team', adminKey, fd);
      toast.success('Membre ajouté');
      setForm({ agency: form.agency, prenom: '', fonction: '', order: 0 });
      setPhoto(null);
      fetchTeam();
    } catch (err) {
      reportError('ajout membre', err, (m) => toast.error(m || "Erreur lors de l'ajout"));
    }
  };

  const deleteMember = async (id) => {
    try {
      await adminDelete(`/api/admin/team/${id}`, adminKey);
      toast.success('Membre supprimé');
      fetchTeam();
    } catch (err) {
      reportError('suppression membre', err, () => toast.error('Erreur lors de la suppression'));
    }
  };

  const uploadLogo = async (file) => {
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append('logo', file);
      await adminUpload('put', `/api/admin/agency-logo/${AGENCY_ID}`, adminKey, fd);
      setLogoSent(true);
      toast.success('Logo Artois mis à jour');
    } catch (err) {
      reportError('upload logo', err, (m) => toast.error(m || "Erreur lors de l'envoi du logo"));
    }
  };

  const purgeDemo = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer TOUTES les données de démonstration ?')) return;
    try {
      const res = await adminDelete('/api/admin/purge-demo', adminKey, { confirm: 'PURGE-DEMO' });
      toast.success(`Données de démo supprimées : ${JSON.stringify(res.data.deleted)}`);
      fetchTeam();
    } catch (err) {
      reportError('purge démo', err, () => toast.error('Erreur lors de la purge'));
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-team-tab">
      {isSuperAdmin && (
        <Card className="p-5 border-red-200 bg-red-50/30">
          <h3 className="font-['Space_Grotesk'] font-semibold mb-2 text-red-700">Purger les données de démonstration</h3>
          <p className="text-xs text-muted-foreground mb-3">Supprime tous les membres d'équipe, photos, avis et actualités de démonstration (données Unsplash/test). Cette action est irréversible.</p>
          <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-50" onClick={purgeDemo}>
            <XCircle className="h-4 w-4 mr-2" /> Purger toutes les données de démo
          </Button>
        </Card>
      )}

      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold mb-4">Logo de l'agence</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium block mb-2">Logo Artois</label>
            <input type="file" accept="image/*" ref={logoRef} className="hidden" onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <Button variant="outline" size="sm" className="w-full" onClick={() => logoRef.current?.click()}>
              <Image className="h-4 w-4 mr-2" /> {logoSent ? 'Logo envoyé !' : 'Choisir le logo Artois'}
            </Button>
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <h3 className="font-['Space_Grotesk'] font-semibold mb-4">Ajouter un membre</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 items-end">
          <div>
            <label className="text-xs font-medium block mb-1">Agence</label>
            <Select value={form.agency} onValueChange={(v) => setForm((p) => ({ ...p, agency: v }))}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value={AGENCY_ID}>Artois.Ptitservices</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Prénom *</label>
            <Input className="h-9" value={form.prenom} onChange={(e) => setForm((p) => ({ ...p, prenom: e.target.value }))} placeholder="Prénom" data-testid="team-prenom-input" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Fonction *</label>
            <Input className="h-9" value={form.fonction} onChange={(e) => setForm((p) => ({ ...p, fonction: e.target.value }))} placeholder="Ex: Easer, Responsable..." data-testid="team-fonction-input" />
          </div>
          <div>
            <label className="text-xs font-medium block mb-1">Photo</label>
            <input type="file" accept="image/*" ref={photoRef} className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
            <Button variant="outline" size="sm" className="w-full h-9" onClick={() => photoRef.current?.click()}>
              <Image className="h-3.5 w-3.5 mr-1" /> {photo ? photo.name.substring(0, 15) : 'Choisir'}
            </Button>
          </div>
          <Button size="sm" className="h-9" onClick={addMember} data-testid="team-add-btn">Ajouter</Button>
        </div>
      </Card>

      {error && <Card className="p-4 text-sm text-destructive">{error}</Card>}
      {!error && members.length === 0 ? (
        <Card className="p-8 text-center"><p className="text-muted-foreground">Aucun membre d'équipe. Ajoutez-en un ci-dessus.</p></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m) => (
            <Card key={m._id} className="p-4 flex items-center gap-3" data-testid="team-member-card">
              {m.photo ? (
                <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0 border-2 border-[hsl(var(--primary))]/20">
                  <img src={m.photo} alt={m.prenom} className="w-full h-full object-cover" />
                </div>
              ) : (
                <div className="w-14 h-14 rounded-full bg-[hsl(var(--brand-mint))] flex items-center justify-center flex-shrink-0">
                  <User className="h-6 w-6 text-[hsl(var(--primary))]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm">{m.prenom}</p>
                <p className="text-xs text-muted-foreground">{m.fonction}</p>
                <Badge variant="secondary" className="text-[10px] mt-1">{m.agency}</Badge>
              </div>
              <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 flex-shrink-0" onClick={() => deleteMember(m._id)} aria-label="Supprimer">
                <XCircle className="h-4 w-4" />
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
